import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CONDITIONS, SYMPTOMS, TREATMENTS, GOALS, STAGES, AGE_RANGES } from "@/lib/constants";
import usePageMeta from "@/lib/usePageMeta";

const STEPS = [
  { key: "stage", title: "Where are you in your journey?", subtitle: "Choose the stage that most fits right now.", type: "single", options: STAGES.map(s => s.label), values: STAGES.map(s => s.id) },
  { key: "conditions", title: "Which conditions are part of your story?", subtitle: "Pick all that apply.", type: "multi", options: CONDITIONS },
  { key: "symptoms", title: "What symptoms have you navigated?", subtitle: "This helps us find truly similar peers.", type: "multi", options: SYMPTOMS },
  { key: "treatments", title: "What have you tried?", subtitle: "Treatments, therapies, lifestyle shifts.", type: "multi", options: TREATMENTS },
  { key: "goals", title: "What are you working toward?", subtitle: "Your goals shape your matches.", type: "multi", options: GOALS },
  { key: "age_range", title: "Your age range?", subtitle: "Kept private. Used only for matching.", type: "single", options: AGE_RANGES, values: AGE_RANGES },
  { key: "bio", title: "One line about you.", subtitle: "Anonymous. Optional. Warm.", type: "text" },
];

export default function Onboarding() {
  usePageMeta({ title: "Onboarding", description: "Build your Wingwomen profile so we can match you with the women whose journey mirrors yours." });
  const [i, setI] = useState(0);
  const [data, setData] = useState({
    conditions: [], symptoms: [], treatments: [], goals: [], lifestyle: [],
    stage: null, age_range: null, bio: "",
  });
  const [saving, setSaving] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();
  const step = STEPS[i];

  const toggle = (key, val) => {
    setData(d => {
      const arr = d[key] || [];
      return { ...d, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
    });
  };
  const set = (key, val) => setData(d => ({ ...d, [key]: val }));

  const next = async () => {
    if (i < STEPS.length - 1) return setI(i + 1);
    setSaving(true);
    try {
      await api.post("/profile", data);
      await refresh();
      toast.success("Your circle is being formed…");
      nav("/app");
    } catch (e) { toast.error("Could not save"); }
    setSaving(false);
  };

  const canProceed = (() => {
    if (step.type === "multi") return (data[step.key] || []).length > 0;
    if (step.type === "single") return !!data[step.key];
    return true;
  })();

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* progress */}
        <div className="flex items-center gap-1.5 mb-10">
          {STEPS.map((_, idx) => (
            <div key={idx} data-testid={`step-dot-${idx}`}
                 className={`h-1 flex-1 rounded-full transition-colors ${idx <= i ? "bg-coral" : "bg-border"}`} />
          ))}
        </div>

        <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4">
          <span className="rule" /> Step {i + 1} of {STEPS.length}
        </div>

        <h1 className="font-display text-4xl sm:text-5xl leading-tight" data-testid="onb-title">{step.title}</h1>
        <p className="mt-3 text-ink/70">{step.subtitle}</p>

        <div className="mt-10 min-h-[240px]" key={i}>
          {step.type === "multi" && (
            <div className="flex flex-wrap gap-2 animate-fade-up" data-testid="onb-multi">
              {step.options.map(opt => {
                const active = data[step.key]?.includes(opt);
                return (
                  <button key={opt} type="button" onClick={() => toggle(step.key, opt)}
                    data-testid={`chip-${step.key}-${opt}`}
                    className={`px-4 py-2 rounded-full border text-sm transition-colors ${
                      active ? "bg-sage text-white border-sage" : "bg-white border-border hover:border-ink/40"
                    }`}>
                    {active && <Check size={14} className="inline mr-1.5" weight="bold" />}
                    {opt}
                  </button>
                );
              })}
            </div>
          )}
          {step.type === "single" && (
            <div className="grid sm:grid-cols-2 gap-3 animate-fade-up" data-testid="onb-single">
              {step.options.map((opt, idx) => {
                const val = step.values[idx];
                const active = data[step.key] === val;
                return (
                  <button key={val} type="button" onClick={() => set(step.key, val)}
                    data-testid={`option-${step.key}-${val}`}
                    className={`text-left p-5 rounded-xl border transition-colors ${
                      active ? "border-coral bg-coral-50" : "border-border bg-white hover:border-ink/40"
                    }`}>
                    <div className="font-display text-lg">{opt}</div>
                  </button>
                );
              })}
            </div>
          )}
          {step.type === "text" && (
            <textarea
              rows={4} value={data.bio} onChange={(e) => set("bio", e.target.value)}
              data-testid="onb-bio"
              maxLength={200}
              placeholder="e.g. 3 years into my endo journey, finally trying again after surgery."
              className="w-full bg-white border border-border rounded-xl p-4 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none animate-fade-up"
            />
          )}
        </div>

        <div className="mt-10 flex items-center justify-between">
          <button type="button" disabled={i === 0} onClick={() => setI(i - 1)}
            data-testid="onb-back"
            className="flex items-center gap-2 text-sm text-muted-foreground disabled:opacity-40 hover:text-ink transition-colors">
            <ArrowLeft size={16} /> Back
          </button>
          <button type="button" onClick={next} disabled={!canProceed || saving}
            data-testid="onb-next"
            className="flex items-center gap-2 bg-coral text-white px-6 py-3 rounded-full font-medium hover:bg-coral-600 disabled:opacity-50 transition-colors">
            {i === STEPS.length - 1 ? (saving ? "Saving…" : "Enter Wingwomen") : "Continue"}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
